import { Alert, Divider, Form, Spin, Typography } from 'antd';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import {
  LicenseCreationAttributes,
  LicenseCreationAttributesStore,
  LicenseManagerAssignmentAttributes,
  getUnlimitedLicenseAttributes,
} from '../../../stores/licensing-store';
import NewItemModal from 'src/core/ui/newitemmodal';
import LicenseAttributesComponent from 'src/pages/licenses/license-attributes/license-attributes';
import { IdentityType } from 'src/common/types';
import RoleAssignmentAttributesComponent from './role-attributes';
import { RoleAssignmentAttributes } from 'src/stores/role-assignments-store';
const { Text } = Typography;

const UpdateRoleAttributesDialog: FC<{
  identityId: string;
  scopeId: string;
  onClose: (newExpirationDate?: Date) => void;
  roleExpirationDate?: Date;
  identityType: IdentityType;
  editLicenseManagerRoleAttributes: boolean;
}> = ({ identityId, scopeId, onClose, roleExpirationDate, identityType, editLicenseManagerRoleAttributes }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const inputRef = useRef();
  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesState = licenseCreationAttributesStore.state;
  /*const [customError, setCustomError] = useState('');*/
  const [licenseAttributes, setLicenseAttributes] = useState<LicenseManagerAssignmentAttributes>();
  const [licenseAttributesErrors, setLicenseAttributesErrors] = useState(false);
  const unlimitedLicenseDefaults = getUnlimitedLicenseAttributes();
  const [roleAttributes, setRoleAttributes] = useState<RoleAssignmentAttributes | undefined>(
    roleExpirationDate ? { expirationDate: roleExpirationDate } : undefined
  );

  const onRoleAttributesChange = (item: RoleAssignmentAttributes) => {
    setRoleAttributes(item);
  };

  useEffect(() => {
    if (editLicenseManagerRoleAttributes) licenseCreationAttributesStore.getByUserScope({ identityId: identityId, scopeId: scopeId, identityType });
  }, [inputRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const createLicenseAttributes = async () => {
    if (licenseAttributes) {
      var item: LicenseCreationAttributes = {
        identityType: identityType,
        id: licenseCreationAttributesState.item.value.id,
        maximumLicenseDueDate: licenseAttributes.maximumLicenseDueDate,
        maxLicensesPeriodInDays: licenseAttributes.maxLicensesPeriodInDays,
        maxNumberOfLicensesPerMonth: licenseAttributes.maxNumberOfLicensesPerMonth,
        allowedDevices: licenseAttributes.allowedDevices,
        identityId: identityId,
        scopeId: scopeId,
        needsSignOff: licenseAttributes.needsSignOff,
        canCreateRecurrentLicenses: licenseAttributes.canCreateRecurrentLicenses ?? false,
        canCreateUnlimitedLicense: licenseAttributes.canCreateUnlimitedLicense,
        maxNumberOfLicensesInCurrentMonth: licenseCreationAttributesState.item.value.maxNumberOfLicensesInCurrentMonth,
      };
      if (item.canCreateUnlimitedLicense) {
        item.maximumLicenseDueDate = unlimitedLicenseDefaults.untilDate;
        item.maxLicensesPeriodInDays = unlimitedLicenseDefaults.maxPeriodInDays;
      }
      let response = await licenseCreationAttributesStore.save(licenseCreationAttributesState.item.id.value, item);
      if (response?.isSuccess) {
        form.resetFields();
        onClose(roleAttributes?.expirationDate);
      }
    } else if (roleAttributes?.expirationDate) {
      onClose(roleAttributes?.expirationDate);
    }
  };
  const onLicenseAttributesChange = (item: LicenseManagerAssignmentAttributes) => {
    setLicenseAttributes(item);
  };
  return (
    <NewItemModal
      width={600}
      className="figma-modal"
      title={t('UpdateRoleAttributes')}
      onNewItem={
        editLicenseManagerRoleAttributes
          ? createLicenseAttributes
          : async () => {
              onClose(roleAttributes?.expirationDate);
            }
      }
      onClose={() => {
        onClose();
      }}
      onOkButtonIsDisabled={licenseCreationAttributesState.isBusy.get() || licenseAttributesErrors || !roleAttributes?.expirationDate}
      onCancelButtonIsDisabled={licenseCreationAttributesState.isBusy.get()}
      closable={!licenseCreationAttributesState.isBusy.get()}
      maskClosable={!licenseCreationAttributesState.isBusy.get()}
      okText={t('Update')}
      errorMessage={licenseCreationAttributesState.errorMessage}
      body={
        <>
          {licenseCreationAttributesState.errorMessage.value && licenseCreationAttributesState.errorMessage.value && (
            <Alert type="error" message={t('ErrorLoadingLicense')} />
          )}
          {
            <Spin spinning={licenseCreationAttributesState.isBusy.get()}>
              {((editLicenseManagerRoleAttributes && licenseCreationAttributesState.item.value) || !editLicenseManagerRoleAttributes) && (
                <Form size="middle" layout="vertical" autoComplete="off">
                  <Text style={{ marginBottom: 10 }} strong>
                    {t('RoleAssignmentAttributes')}:
                  </Text>
                  <RoleAssignmentAttributesComponent onChange={onRoleAttributesChange} initialValue={roleExpirationDate} />
                  {editLicenseManagerRoleAttributes && (
                    <>
                      <Divider />
                      <Text style={{ marginBottom: 10, textAlign: 'right' }} strong>
                        {t('LicenseManagerRoleAttributes')}:
                      </Text>{' '}
                      <LicenseAttributesComponent
                        onChange={onLicenseAttributesChange}
                        initialAttr={licenseCreationAttributesState.item.value as LicenseCreationAttributes}
                        roleExpirationDate={roleExpirationDate}
                        onFormValidation={(errors) => setLicenseAttributesErrors(errors)}
                      />
                    </>
                  )}
                </Form>
              )}
            </Spin>
          }
        </>
      }
    />
  );
};

export default UpdateRoleAttributesDialog;
